import React, { useState, useEffect } from 'react';
import { clientiAPI } from '../lib/api';
import { formatDate, getStatusLabel } from '../lib/utils';
import { StatusBadge } from '../components/common/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Pencil, 
  Trash2, 
  Building2,
  Loader2
} from 'lucide-react';

const initialFormData = {
  ragione_sociale: '',
  partita_iva: '',
  settore: '',
  referente: '',
  email: '',
  telefono: '',
  stato: 'attivo'
};

export const ClientiPage = () => {
  const [clienti, setClienti] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadClienti();
  }, []);

  const loadClienti = async () => {
    try {
      const response = await clientiAPI.getAll();
      setClienti(response.data);
    } catch (error) {
      console.error('Error loading clienti:', error);
      toast.error('Errore nel caricamento dei clienti');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (cliente = null) => {
    if (cliente) {
      setEditingCliente(cliente);
      setFormData({
        ragione_sociale: cliente.ragione_sociale || '',
        partita_iva: cliente.partita_iva || '',
        settore: cliente.settore || '',
        referente: cliente.referente || '',
        email: cliente.email || '',
        telefono: cliente.telefono || '',
        stato: cliente.stato || 'attivo'
      });
    } else {
      setEditingCliente(null);
      setFormData(initialFormData);
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingCliente(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (editingCliente) {
        await clientiAPI.update(editingCliente.id, formData);
        toast.success('Cliente aggiornato con successo');
      } else {
        await clientiAPI.create(formData);
        toast.success('Cliente creato con successo');
      }
      handleCloseDialog();
      loadClienti();
    } catch (error) {
      console.error('Error saving cliente:', error);
      toast.error('Errore nel salvataggio del cliente');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (cliente) => {
    if (window.confirm(`Sei sicuro di voler eliminare "${cliente.ragione_sociale}"?`)) {
      try {
        await clientiAPI.delete(cliente.id);
        toast.success('Cliente eliminato con successo');
        loadClienti();
      } catch (error) {
        console.error('Error deleting cliente:', error);
        toast.error('Errore nell\'eliminazione del cliente');
      }
    }
  };

  const filteredClienti = clienti.filter(cliente =>
    cliente.ragione_sociale.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (cliente.settore && cliente.settore.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (cliente.referente && cliente.referente.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="clienti-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clienti-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Clienti</h1>
          <p className="text-muted-foreground">Gestisci l'anagrafica clienti</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-cliente-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nuovo Cliente
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Cerca clienti..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-clienti-input"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ragione Sociale</TableHead>
                <TableHead>Settore</TableHead>
                <TableHead>Referente</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredClienti.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">Nessun cliente trovato</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredClienti.map((cliente) => (
                  <TableRow key={cliente.id} data-testid={`cliente-row-${cliente.id}`}>
                    <TableCell className="font-medium">{cliente.ragione_sociale}</TableCell>
                    <TableCell>{cliente.settore || '-'}</TableCell>
                    <TableCell>{cliente.referente || '-'}</TableCell>
                    <TableCell>{cliente.email || '-'}</TableCell>
                    <TableCell>
                      <StatusBadge status={cliente.stato} />
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`cliente-actions-${cliente.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenDialog(cliente)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Modifica
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(cliente)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md" data-testid="cliente-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingCliente ? 'Modifica Cliente' : 'Nuovo Cliente'}
            </DialogTitle>
            <DialogDescription>
              {editingCliente ? 'Modifica i dati del cliente' : 'Inserisci i dati del nuovo cliente'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ragione_sociale">Ragione Sociale *</Label>
              <Input
                id="ragione_sociale"
                value={formData.ragione_sociale}
                onChange={(e) => setFormData({...formData, ragione_sociale: e.target.value})}
                required
                data-testid="cliente-ragione-sociale-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="partita_iva">Partita IVA</Label>
                <Input
                  id="partita_iva"
                  value={formData.partita_iva}
                  onChange={(e) => setFormData({...formData, partita_iva: e.target.value})}
                  data-testid="cliente-piva-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="settore">Settore</Label>
                <Input
                  id="settore"
                  value={formData.settore}
                  onChange={(e) => setFormData({...formData, settore: e.target.value})}
                  placeholder="Es. IT, Finance..."
                  data-testid="cliente-settore-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="referente">Referente</Label>
              <Input
                id="referente"
                value={formData.referente}
                onChange={(e) => setFormData({...formData, referente: e.target.value})}
                data-testid="cliente-referente-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  data-testid="cliente-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefono">Telefono</Label>
                <Input
                  id="telefono"
                  value={formData.telefono}
                  onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                  data-testid="cliente-telefono-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="stato">Stato</Label>
              <Select 
                value={formData.stato} 
                onValueChange={(value) => setFormData({...formData, stato: value})}
              >
                <SelectTrigger data-testid="cliente-stato-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="attivo">Attivo</SelectItem>
                  <SelectItem value="non_attivo">Non Attivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Annulla
              </Button>
              <Button type="submit" disabled={isSubmitting} data-testid="cliente-submit-btn">
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  editingCliente ? 'Salva Modifiche' : 'Crea Cliente'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientiPage;
